import React, { useState } from 'react';

import {
    Autocomplete,
    Box,
    Chip,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@sunwisesoftware/sunwise-ui';
import i18next from 'i18next';
import capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const optionInputConverter = (value, selectedOption) =>
    selectedOption === 'year' ? value * 12 : value;

const optionLabelConverter = (value, selectedOption) =>
    selectedOption === 'year' ? parseInt(value / 12) : value;

const Terms = ({
    control = {},
    defaultOptionValue = 'month',
    disabled = false,
    label = '',
    name = 'parameters.terms',
    placeholderText = i18next.t('Press enter to add'),
    size = 'small',
}) => {
    const { t } = useTranslation();
    const [selectedOption, setSelectedOption] = useState(defaultOptionValue);

    const onChangeTermsFormat = (format) => setSelectedOption(format);

    const renderValue = (value, getItemProps) =>
        value.map((value, index) => {
            const { key, ...itemProps } = getItemProps({ index });
            const displayValue = optionLabelConverter(value, selectedOption);
            const label = `${displayValue} ${t(`${capitalize(selectedOption)}`, { count: parseInt(displayValue) })}`;
            return <Chip {...itemProps} label={label} key={key} />;
        });

    const options = [
        { label: t('Year', { count: 2 }), value: 'year' },
        { label: t('Month', { count: 2 }), value: 'month' },
    ];

    const onChange = ({ field, reason, value }) => {
        let newValue = value;
        if (reason === 'createOption') {
            const lastValue = value[value.length - 1];
            const convertedValue = optionInputConverter(
                lastValue,
                selectedOption,
            );
            newValue = [...value.slice(0, -1), convertedValue];
        }
        field.onChange(newValue.sort((a, b) => a - b));
    };

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <Stack direction="row">
                    <Autocomplete
                        disabled={disabled}
                        freeSolo
                        fullWidth
                        label={label}
                        multiple
                        onChange={(_, value, reason) =>
                            onChange({ field, reason, value })
                        }
                        options={[]}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                disabled={disabled}
                                error={Boolean(error)}
                                fullWidth
                                helperText={error?.message}
                                inputProps={{
                                    ...params.inputProps,
                                    min: 1,
                                }}
                                label={label}
                                placeholder={placeholderText}
                                type="number"
                            />
                        )}
                        renderValue={renderValue}
                        size={size}
                        value={field.value}
                    />

                    <Box>
                        <Select
                            defaultValue={defaultOptionValue}
                            disabled={disabled}
                            onChange={(e) =>
                                onChangeTermsFormat(e.target.value, field)
                            }
                            size={size}
                            value={selectedOption}
                        >
                            {options.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    value={option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                </Stack>
            )}
        />
    );
};

Terms.propTypes = {
    control: PropTypes.object.isRequired,
    defaultOptionValue: PropTypes.string,
    disabled: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    placeholderText: PropTypes.string,
    size: PropTypes.string,
};

export default Terms;
